import { z } from 'zod';
import {
  IDEMPOTENCY_HEADER,
  handoverListSchema,
  handoverResultSchema,
  pickPlanSchema,
  storedFileSchema,
  type FileContext,
  type HandoverResultDto,
  type HandoverViewDto,
  type PickPlanDto,
  type StoredFileDto,
} from '@eombor/contracts';
import { apiClient } from '../../auth/model/auth.store.js';
import type { ApiClient } from '../../../shared/api/client.js';

/**
 * Berish oqimining endpointlari. Manba:
 *   `requisition/infrastructure/http/handover.controller.ts`
 *   `files/infrastructure/http/file.controller.ts`
 *
 * Berish umumiy `POST /:id/transition` orqali BAJARILMAYDI: u o'tish emas,
 * hujjat — unda foto-hisobot, koordinata va har satr uchun haqiqatda
 * berilgan miqdor bor.
 */

/** `createHandoverSchema` ning sim ustidagi ko'rinishi. */
export interface CreateHandoverBody {
  /* Oluvchining ismi va lavozimi bu yerda YO'Q: ismni server
     talabnomaning «Kim orqali» sidan oladi, lavozim esa umuman
     so'ralmaydi (`createHandoverSchema` izohiga qarang). */
  photoFileIds: string[];
  gps?: { lat: number; lng: number };
  /**
   * `note` — CHETLANISH sababi: tasdiqlangandan kam berilganda majburiy.
   *
   * Satrda, hujjat izohida emas: uch satrli talabnomada bitta umumiy
   * izoh qaysi satrga tegishli ekanini yo'qotardi.
   */
  lines: { lineId: string; issuedQty: number; note?: string }[];
  comment?: string;
}

export interface ConfirmReceiptBody {
  photoFileIds?: string[];
  comment?: string;
}

/** Lavozim ma'lumotnomasining bitta satri (`org/application/position.service.ts`). */
export const positionSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  isActive: z.boolean(),
});

export type PositionDto = z.infer<typeof positionSchema>;

const positionPageSchema = z.object({
  items: z.array(positionSchema),
  total: z.number(),
  page: z.number(),
  limit: z.number(),
});

export interface HandoverApi {
  /** Talabnomaning dalolatnomalari — rasm havolalari bilan (5 daqiqa). */
  list: (requisitionId: string) => Promise<HandoverViewDto[]>;
  issue: (
    requisitionId: string,
    body: CreateHandoverBody,
    idempotencyKey: string,
  ) => Promise<HandoverResultDto>;
  confirmReceipt: (
    requisitionId: string,
    body: ConfirmReceiptBody,
    idempotencyKey: string,
  ) => Promise<HandoverResultDto>;
  /** Yig'ib olish rejasi — «qayerga borib, nimani olaman». */
  pickPlan: (requisitionId: string) => Promise<PickPlanDto>;
  /** Foto-hisobot rasmini yuklaydi — dalolatnomaga `id` biriktiriladi. */
  uploadPhoto: (file: File, context: FileContext) => Promise<StoredFileDto>;
  positions: () => Promise<PositionDto[]>;
}

const path = (id: string): string => `/requisitions/${encodeURIComponent(id)}`;

export const createHandoverApi = (client: ApiClient): HandoverApi => ({
  list: (requisitionId) =>
    client.apiFetch(`${path(requisitionId)}/handovers`, { schema: handoverListSchema }),

  issue: (requisitionId, body, idempotencyKey) =>
    client.apiFetch(`${path(requisitionId)}/handover`, {
      method: 'POST',
      headers: { [IDEMPOTENCY_HEADER]: idempotencyKey },
      body: JSON.stringify(body),
      schema: handoverResultSchema,
    }),

  confirmReceipt: (requisitionId, body, idempotencyKey) =>
    client.apiFetch(`${path(requisitionId)}/confirm-receipt`, {
      method: 'POST',
      headers: { [IDEMPOTENCY_HEADER]: idempotencyKey },
      body: JSON.stringify(body),
      schema: handoverResultSchema,
    }),

  pickPlan: (requisitionId) =>
    client.apiFetch(`${path(requisitionId)}/pick`, { schema: pickPlanSchema }),

  uploadPhoto: (file, context) => {
    const form = new FormData();
    form.set('context', context);
    form.set('file', file);
    // `Content-Type` ATAYLAB qo'yilmaydi — uni brauzer `boundary` bilan
    // birga o'zi yozadi (`shared/api/client.ts`).
    return client.apiFetch('/files', { method: 'POST', body: form, schema: storedFileSchema });
  },

  positions: async () =>
    (await client.apiFetch('/positions?page=1&limit=200', { schema: positionPageSchema })).items,
});

export const handoverApi = createHandoverApi(apiClient);
